import { markRaw, type Component } from "vue";
import router from "@/routes";
import { useSurveyStore } from "@stores/survey/survey";
import { useEmailCampaignStore } from "@stores/survey/mailing/emailCampaign";
import { useSurveysStore } from "@stores/surveys";
import { useJcdCampaignsStore } from "@stores/jcdCampaigns";
import { useExportsStore } from "@stores/survey/exports";
import { usePricerProjectStore } from "@stores/pricer/pricerProject";
import { useToastsStore } from "@/stores/toastsStore";
import useMyFetch from "@stores/useMyFetch";
import { downloadBlob } from "@utils/toolBox";
import { hubspotQuoteUrl } from "@utils/pricer/hubspotUrls";
import type { PricerProject } from "@/types";
import MCampaignPushToast from "@/components/MCampaign/Pushes/MCampaignPushToast.vue";
import { useMCampaignPushesStore } from "@stores/mCampaignPushes";
import { useSurveyContextStore } from "@stores/survey/surveyContext";

interface TaskInfo {
  type?: string;
  campaignToken?: string;
  campaignId?: number;
  questionId?: number;
  exportToken?: string;
  surveyToken?: string;
  projectToken?: string;
  hsQuoteId?: number;
  mCampaignToken?: string;
}

interface TaskProgress {
  id: string;
  status: string;
  taskInfo?: TaskInfo;
}

interface TaskHandler {
  onComplete?: (task: TaskProgress) => void | Promise<void>;
  onFailed?: (task: TaskProgress) => void;
  toastComponent?: Component;
}

const refreshEmailCampaign = (task: TaskProgress) => {
  if (router.currentRoute.value.name !== "EmailCampaignEdit") return;

  useEmailCampaignStore().getEmailCampaign(task.taskInfo?.campaignToken);
};

const refreshPushHistory = (task: TaskProgress) => {
  const token = task.taskInfo?.mCampaignToken;
  const { name, params } = router.currentRoute.value;
  if (!token || name !== "MCampaignPushes" || params.token !== token) return;

  useMCampaignPushesStore().fetchPushes(token);
};

const refreshSurveyContext = (task: TaskProgress) => {
  const token = task.taskInfo?.surveyToken;
  const { name, params } = router.currentRoute.value;
  if (!token || name !== "SurveyContext" || params.token !== token) return;

  useSurveyContextStore().refreshIfGenerating(token);
};

const refreshJcdCampaigns: TaskHandler = {
  onComplete: () => {
    if (router.currentRoute.value.name !== "JcdCampaigns") return;

    useJcdCampaignsStore().fetch(1);
  },
};

// Per task-type behaviour for the global progress toaster, keyed by `taskInfo.type`.
// Each entry may declare:
//   - onComplete(task): a side effect to run when the task finishes (e.g. refresh a list)
//   - onFailed(task): a side effect to run when the task fails
//   - toastComponent: a component rendering the toast instead of the generic one (receives `task` and `status`)
// Adding a new background-task behaviour = one entry here; the toaster stays generic.
export const TASK_HANDLERS: Record<string, TaskHandler> = {
  import_survey_data: {
    // Refresh the surveys list, but only while the user is on the index page.
    // Anywhere else, the list is re-fetched by the index route's beforeEnter guard.
    onComplete: () => {
      if (router.currentRoute.value.name !== "root") return;

      useSurveysStore().fetch();
    },
  },
  email_campaign_schedule: { onComplete: refreshEmailCampaign, onFailed: refreshEmailCampaign },
  push_m_campaign: {
    toastComponent: markRaw(MCampaignPushToast),
    onComplete: refreshPushHistory,
    onFailed: refreshPushHistory,
  },
  survey_context: { onComplete: refreshSurveyContext, onFailed: refreshSurveyContext },
  jcd_campaigns_import: refreshJcdCampaigns,
  jcd_generate_survey: refreshJcdCampaigns,
  jcd_generate_ppt: {
    onComplete: async (task) => {
      if (task.taskInfo?.campaignId === undefined) return;

      const campaign = await useJcdCampaignsStore().refreshCampaign(task.taskInfo.campaignId);
      if (campaign?.ppt_report_url) window.open(campaign.ppt_report_url, "_blank");
    },
  },
  media_archive: {
    onComplete: async (task) => {
      const { response, data } = await useMyFetch(
        `/questions/${task.taskInfo?.questionId}/media?task_progress_id=${task.id}`,
      )
        .get()
        .blob();
      if (response.value?.ok && data.value) downloadBlob(data.value, response.value, "media.zip");
    },
  },
  export: {
    onComplete: async (task) => {
      const exportsStore = useExportsStore();
      // Every tab receives the completion; only the one that launched the task downloads.
      if (exportsStore.claimLaunchedTask(task.id) && task.taskInfo?.exportToken) {
        await exportsStore.downloadExport(task.taskInfo.exportToken, task.taskInfo.surveyToken);
      }
      if (router.currentRoute.value.name === "SurveyExports") exportsStore.fetchExports();
    },
    onFailed: (task) => useExportsStore().claimLaunchedTask(task.id),
  },
  export_cards: {
    onComplete: async (task) => {
      useSurveyStore().isExportingCards = false;
      const { response, data } = await useMyFetch(
        `/surveys/${task.taskInfo?.surveyToken}/cards_export/download?task_progress_id=${task.id}`,
      )
        .get()
        .blob();
      if (response.value?.ok && data.value) downloadBlob(data.value, response.value, "questionnaire.docx");
    },
    onFailed: () => (useSurveyStore().isExportingCards = false),
  },
  pricer_hubspot_quote: {
    onComplete: (task) => {
      const { hsQuoteId, projectToken } = task.taskInfo ?? {};
      if (hsQuoteId === undefined) return;

      const projectStore = usePricerProjectStore();
      const current: Partial<PricerProject> = projectStore.project;
      if (current.token === projectToken) {
        Object.assign(projectStore.project, { hs_quote_id: hsQuoteId });
        Object.assign(projectStore.initialProject, { hs_quote_id: hsQuoteId });
      }
      useToastsStore().update(`task-${task.id}`, {
        actions: [{ label: "Open quote in HubSpot", onClick: () => window.open(hubspotQuoteUrl(hsQuoteId), "_blank") }],
      });
    },
  },
};
